import { State } from '../@types';
import { isWeb } from '../helpers/isWeb';

export const selectAreas = (store: State) => store.appData.areas;
export const selectDialog = (store: State) => store.appData.common.dialog;
export const selectOpenedModal = (store: State) =>
  store.appData.common.openedModal;
export const selectIsVerifiedUser = (store: State) =>
  store.appData.common.firstSteps;
export const selectBrandSettings = (store: State) =>
  store.appData.common.brandSettings;

export const selectBicMandatory = (store: State) =>
  store.appData.common.brandSettings.bicMandatory;

export const selectBicMandatoryForIbanRegex = (store: State) =>
  store.appData.common.brandSettings?.bicMandatoryForIbanRegex;

export const selectAllowedSEPACountries = (store: State) =>
  store.appData.common.brandSettings.allowedSEPACountries;

export const selectCommon = (store: State) => store.appData.common;
export const selectFeatures = (store: State) => store.appData.features;

/**
 * The booking portal never takes fleet styling.
 *
 * `fleetStyle` is per-AREA, keyed off the selected car — so picking a car in a
 * Zhero or OBI area repaints whatever screen is showing it. That is right
 * inside the app, and wrong for `/portal`: it is a brand surface embedded on a
 * client's own page and has to stay in the brand's colours whatever car the
 * user happens to choose.
 *
 * Disabled here, at the source, rather than per component — fleet style is read
 * in a dozen places and any new one would otherwise have to remember to opt
 * out. Route-sniffing mirrors `isDashboardScreen()` in onBoardingHelper.
 */
const isPortalScreen = () => {
  if (!isWeb()) return false;
  if (document.location.pathname.startsWith('/portal')) return true;
  // Screens the portal round-trips through (login, select payment, add card,
  // billing) carry ?returnTo=/portal — fleet styling stays off for the whole
  // trip, or the brand button flips to the fleet colour of whatever car the
  // portal left selected in the map state.
  try {
    const returnTo = new URLSearchParams(document.location.search).get(
      'returnTo'
    );
    return !!returnTo && returnTo.startsWith('/portal');
  } catch {
    return false;
  }
};

/** The `fleetStyle` of the area the selected (or rented) car belongs to. */
const selectedCarFleetStyle = (store: State): string | undefined => {
  if (isPortalScreen()) return undefined;
  return (
    (Array.isArray(store.appData.areas) &&
      store.appData.areas.find((area) =>
        [
          store.ui.map.selectedCar?.areaId,
          store.userData.rental?.rentalData?.carData?.areaId,
        ].includes(area?.id)
      )?.fleetStyle) ||
    undefined
  );
};

export const selectIsOBI = (store: State) =>
  selectedCarFleetStyle(store) === 'obi';

export const selectIsExpert = (store: State) =>
  selectedCarFleetStyle(store) === 'expert';

export const selectIsSPP = (store: State) =>
  selectedCarFleetStyle(store) === 'spp';

export const selectIsBUB = (store: State) =>
  selectedCarFleetStyle(store) === 'bub';

export const selectIsZhero = (store: State) =>
  selectedCarFleetStyle(store) === 'zhero';

export const selectIsJysk = (store: State) =>
  selectedCarFleetStyle(store) === 'jysk';

export const selectIsLuckyCar = (store: State) =>
  selectedCarFleetStyle(store) === 'luckyCar';

export const selectFleetStyle = (store: State) =>
  selectedCarFleetStyle(store) ||
  // The car's own fleetStyle is a fallback for when its area isn't loaded yet;
  // suppressed on the portal for the same reason as the area lookup above.
  (isPortalScreen()
    ? undefined
    : store.ui.map.selectedCar?.fleetStyle ||
      store.userData.rental?.rentalData?.carData?.fleetStyle) ||
  'default';

export const selectSupportContacted = (store: State) =>
  store.appData.common.supportContacted;

export const selectIsIntercomUserRegistered = (store: State) =>
  store.appData.common.isIntercomUserRegistered;

export const selectPois = (store: State) => store.appData.pois;

export const selectServices = (store: State) => store.services;

export const selectServiceTypes = (store: State) => store.appData.serviceTypes;

export const selectTexts = (store: State) => store.appData.texts;
